'use client'

import { Button } from '@workspace/ui/components/button'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import { MessageSquare, X } from 'lucide-react'
import * as React from 'react'

interface FeedbackButtonProps {
  onSubmit: (data: { message: string; pageUrl: string; userAgent: string }) => Promise<void>
  className?: string
}

function FeedbackButton({ onSubmit, className }: FeedbackButtonProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [message, setMessage] = React.useState('')
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (status !== 'submitting') {
          setIsOpen(false)
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, status])

  React.useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        setIsOpen(false)
        setStatus('idle')
        setMessage('')
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [status])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!message.trim() || status === 'submitting') return

    setStatus('submitting')
    try {
      await onSubmit({
        message: message.trim(),
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      })
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-4 right-4 z-40 flex items-center gap-1.5 rounded-full border border-[#f97316]/40 bg-[#f97316]/10 px-3 py-1.5 text-xs text-[#f97316] transition-colors hover:bg-[#f97316]/20',
          className
        )}
      >
        <MessageSquare className="size-3.5" />
        Feedback
      </button>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'fixed bottom-4 left-4 right-4 z-40 rounded-lg border border-border bg-background p-5 shadow-xl sm:left-auto sm:w-96',
        className
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Send Feedback</span>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="text-muted-foreground hover:text-foreground"
          disabled={status === 'submitting'}
        >
          <X className="size-4" />
        </button>
      </div>

      {status === 'success' ? (
        <p className="py-4 text-center text-sm text-green-400">Thanks for your feedback!</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <p className="mb-3 text-xs text-muted-foreground">
            Found a bug, have a suggestion, or something feels off? Let us know — all feedback helps
            us improve.
          </p>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What's on your mind?"
            textareaSize="sm"
            className="mb-3"
            disabled={status === 'submitting'}
            rows={5}
          />
          {status === 'error' && (
            <p className="mb-2 text-xs text-red-400">Something went wrong. Please try again.</p>
          )}
          <Button
            type="submit"
            size="sm"
            className="w-full"
            disabled={!message.trim() || status === 'submitting'}
          >
            {status === 'submitting' ? 'Sending...' : 'Send'}
          </Button>
        </form>
      )}
    </div>
  )
}

export { FeedbackButton }
